// Browser-side Amplitude for tiger.com landing pages. Shares AMPLITUDE_API_KEY
// with the account-side events (src/hooks/useAmplitude) so page views and
// registration/login land in one project and one profile timeline.
import { AMPLITUDE_API_KEY } from '@/constant';

let initialized = false;

// amplitude-js touches window on import, so load it lazily to stay SSR-safe.
const getInstance = () => {
  if (typeof window === 'undefined') return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('amplitude-js');
    // The browser (ESM) build exposes the SDK on `default`; the Node (UMD)
    // build exposes it on the module itself. Support both.
    const amplitude = mod.default || mod;
    return amplitude.getInstance();
  } catch {
    return null;
  }
};

/** Initialize the browser Amplitude SDK once on the client. */
export const initBrowserAmplitude = () => {
  if (initialized) return;
  const instance = getInstance();
  if (!instance) return;
  instance.init(AMPLITUDE_API_KEY, undefined, {
    includeReferrer: true,
    includeUtm: true,
    includeGclid: true,
  });
  initialized = true;
};

/** Log a page-visit event with the referrer of the transition. */
export const trackPageView = () => {
  const instance = getInstance();
  if (!instance) return;
  instance.logEvent('Page View', {
    path: window.location.pathname,
    url: window.location.href,
    referrer: document.referrer || undefined,
  });
};

/**
 * Attach an identity to the browser SDK. Until this runs every landing event is
 * anonymous (device-scoped only), so Amplitude has nothing to merge the visit
 * into once the user registers.
 */
export const identifyAmplitudeUser = (userId: string) => {
  if (!userId) return;
  initBrowserAmplitude();
  const instance = getInstance();
  if (!instance) return;
  instance.setUserId(userId);
};

export const getAmplitudeDeviceId = (): string | undefined => {
  // deviceId is only set after init(), so make sure the SDK is up before reading.
  initBrowserAmplitude();
  const instance = getInstance();
  return instance ? instance.getDeviceId() : undefined;
};

export const getAmplitudeSessionId = (): number | undefined => {
  initBrowserAmplitude();
  const instance = getInstance();
  return instance ? instance.getSessionId() : undefined;
};
